import { useEffect, useRef, useState } from "react";
import { SearchBox } from "../../../components/Search";
import {
  CompareRajasthan,
  RajasthanCollective,
  RajasthanDiscomData,
  RajasthanGenData,
  RajasthanGnaTgna,
  RajasthanIsgs,
  RajBankingData,
  ReEnergyData,
} from "../../../models/rajasthan";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import {
  getRajasthanIsgsData,
  getRajasthanCollectiveData,
  getGnaTgnaData,
  getRajasthanGenerationData,
  getRajasthanDiscomData,
  getRajReData,
  getRajTamData,
  getRajDeep,
  getRajasthanForecastDiscomData,
  getRajBankingData,
} from "../../../Rest_api/DemoRestApi";
import {
  ChartData,
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { BrushStart, ChartType, LegendKey } from "../../../models/chart_model";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { FilterTab } from "../../../components/Filters";
import { titleCase } from "../../../extensions/extension";
import { stringToConsistentColor } from "../../../extensions/string";
import { Discom } from "../../Dashboard/Discom/Discom";
import { DynamicDatePicker } from "../../../components/DatePicker";
import Loading from "../../../components/Loading";
import { renderHourTick } from "../../../components/recharts/components";
import { normalizeDisplayLabel } from "../../../utility/displayText";

interface RajData {
  discomLoad: RajasthanDiscomData[];
  forecastDiscomLoad: RajasthanDiscomData[];
  collective: RajasthanCollective[];
  compare: CompareRajasthan[];
  isgs: RajasthanIsgs[];
  gnaTgna: RajasthanGnaTgna[];
  reGen: ReEnergyData[];
  intraGen: RajasthanGenData[];
  bankingData: RajBankingData[];
}
const KEYS: {
  name: string;
  firstKey: string;
  secondKey: string | undefined;
  isTime: boolean;
}[] = [
  {
    name: "Time Slot",
    firstKey: "time_block",
    secondKey: "date",
    isTime: true,
  },
  { name: "Date", firstKey: "date", secondKey: undefined, isTime: false },
  // { name: "Month", firstKey: "month", secondKey: undefined },
];
const UNITS = ["MW", "MWh"];

const DISCOM_LEGENDS = [
  // Ajmer Discom, Jaipur Discom, Jodhpur Discom
  {
    key: "Ajmer Discom",
    label: "Ajmer Discom",
    stackId: "discom",

    color: ExchangeColors[1],
  },
  {
    key: "Jaipur Discom",
    label: "Jaipur Discom",
    stackId: "discom",

    color: ExchangeColors[2],
  },
  {
    key: "Jodhpur Discom",
    label: "Jodhpur Discom",
    stackId: "discom",

    color: ExchangeColors[3],
  },
];

const TOTAL_DISCOM_LEGENDS = [
  {
    key: "Total Discom",
    label: "Load",
    color: ExchangeColors[1],
    stackId: "discom",
  },
];
const SHORT_TERM_LEGENDS = [
  {
    key: "OA_GNA Buy",
    label: "OA GNA",
    color: ExchangeColors[8],
    stackId: "generation",
  },
  {
    key: "OA_TGNA Buy",
    label: "OA TGNA",
    color: ExchangeColors[7],
    stackId: "generation",
  },
  {
    key: "Collective Buy",
    label: "Collect.",
    color: ExchangeColors[4],
    stackId: "generation",
  },
];
const CONSUMPTION = [
  {
    label: "Total",
    value: TOTAL_DISCOM_LEGENDS,
  },
  {
    label: "By Discom",
    value: DISCOM_LEGENDS,
  },
];
export function OverviewDas({
  dates,
}: {
  dates: { startDate: Date; endDate: Date };
}) {
  const [data, setData] = useState<RajData>({
    discomLoad: [],
    forecastDiscomLoad: [],
    collective: [],
    compare: [],
    isgs: [],
    gnaTgna: [],
    reGen: [],
    intraGen: [],
    bankingData: [],
  });
  const isFirstRender = useRef<boolean>(true);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    _handleFetchData();
  }, [dates]);

  async function _handleFetchData() {
    const startDateStr = convertToString(dates.startDate);
    const endDateStr = convertToString(dates.endDate);
    setLoading(true);
    // Initiate all API calls concurrently
    const [
      isgsData,
      collectiveData,
      gnaTgnaData,
      generationData,
      discomData,
      forecastedDiscomData,
      reGenData,
      tamData,
      deepData,
      bankingData,
    ] = await Promise.all([
      getRajasthanIsgsData({ startDate: startDateStr, endDate: endDateStr }),
      getRajasthanCollectiveData({
        startDate: startDateStr,
        endDate: endDateStr,
      }),
      getGnaTgnaData({ startDate: startDateStr, endDate: endDateStr }),
      getRajasthanGenerationData(startDateStr, endDateStr),
      getRajasthanDiscomData(startDateStr, endDateStr),
      getRajasthanForecastDiscomData(startDateStr, endDateStr, "Model 2"),
      getRajReData({ startDate: startDateStr, endDate: endDateStr }),
      getRajTamData({ startDate: startDateStr, endDate: endDateStr }),
      getRajDeep({ startDate: startDateStr, endDate: endDateStr }),
      getRajBankingData({ startDate: startDateStr, endDate: endDateStr }),
    ]);

    const processedIsgsData = isgsData
      .map((item) => ({
        ...item,
        value_mwh: item.value_mw / 4,
        entity: item.from_utility__name + " " + "ISGS Station",
        type: "ISGS",
        time_block: item.slot,
        month: getMonthFromDate(item.date),
      }))
      .filter((item) => item.value_mw > 0);

    const processedCollectiveData = collectiveData.map((item) => {
      const isBuy = item.value_mw > 0;
      return {
        ...item,
        type: isBuy ? "Collective Buy" : "Collective Sell",
        time_block: item.time_slot,
        exchange: item.exchange__name,
        product: item.product__name,
        price_kwh: item.wt_mcp_rs_kwh,
        entity: item.exchange__name + " " + item.product__name,
        value_mwh: Math.abs(item.value_mw) / 4,
        value_mw: Math.abs(item.value_mw),
        month: getMonthFromDate(item.date),
      };
    });
    const processedGnaTgnaData = gnaTgnaData
      .map((item) => {
        const isBuy = item.value_mw > 0;
        return {
          ...item,
          type: `${item.category__category} ${isBuy ? "Buy" : "Sell"}`,
          entity: item.from_utility__name,
          toEntity: item.to_utility__name,
          value_mwh: Math.abs(item.value_mw) / 4,
          value_mw: Math.abs(item.value_mw),
          month: getMonthFromDate(item.date),
        };
      })
      .filter((item) => item.value_mw > 0);
    const processedGenerationData = generationData
      .map((item) => ({
        ...item,
        type: "Intra Generation",
        value_mwh: item.onBarDS_mw! / 4,
        entity: item.station + " " + "Intra Generator",
        value_mw: item.onBarDS_mw!,
        time_block: item.slot,
        month: getMonthFromDate(item.date),
      }))
      .filter((item) => item.value_mw > 0);

    const processedReData = reGenData.map((item) => ({
      ...item,
      type: item.area + " RE Generation",
      value_mwh: item.value_mw / 4,
      entity: item.energy_type + " RE",
    }));

    const processedTamData = tamData.map((item) => ({
      ...item,
      type: "TAM",
      exchange: item.exchange,
      product: item.product,
      value_mwh: item.mw / 4,
      value_mw: item.mw,
      time_block: item.block,
      month: getMonthFromDate(item.date),
    }));

    const processedDeepData: CompareRajasthan[] = deepData
      .map((item) => {
        return {
          ...item,
          type: `Deep`,
          exchange: "Deep",
          product: "RA",
          entity: item.entity,
          time_block: item.slot,
          toEntity: item.entity,
          value_mwh: Math.abs(item.value_mw) / 4,
          value_mw: Math.abs(item.value_mw),
          month: getMonthFromDate(item.date),
        };
      })
      .filter((item) => item.value_mw > 0);

    const processedBankingData = bankingData.map((item) => ({
      ...item,
      type: "Banking",
      exchange: "Banking",
      product: "Banking",
      value_mwh: item.mw / 4,
      value_mw: item.mw,
      time_block: item.time_block,
      month: getMonthFromDate(item.date),
    }));

    // Combine all processed data
    const newData: CompareRajasthan[] = [
      ...processedIsgsData,
      ...processedCollectiveData,
      ...processedGnaTgnaData,
      ...processedGenerationData,
      ...processedReData,
      ...processedTamData,
      ...processedDeepData,
      ...processedBankingData,
    ];

    setData({
      discomLoad: discomData,
      forecastDiscomLoad: forecastedDiscomData,
      collective: collectiveData,
      compare: newData,
      isgs: isgsData,
      gnaTgna: gnaTgnaData,
      reGen: reGenData,

      intraGen: generationData,
      bankingData: bankingData,
    });
    setLoading(false);
  }
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-12 grid-rows-full md:grid-rows-2 gap-4 h-full w-full px-4  p-4 md:overflow-hidden">
        {loading && <Loading />}
        <div className="dash-card col-span-1 md:col-span-6">
          <SummaryChart data={data.compare} discomData={data.discomLoad} />
          {/* VolPriceChart Component Placeholder */}
        </div>
        {/* Top Right */}
        <div className="dash-card col-span-1 md:col-span-6">
          <GenerationSplit
            intraGen={data.intraGen}
            reGen={data.reGen}
            interGen={data.isgs}
          />
          {/* GenerationSplit Component Placeholder */}
        </div>
        {/* Bottom Left */}
        <div className="dash-card col-span-1 md:col-span-6">
          <ProcurementChart data={data.compare} />
        </div>
        {/* Bottom Center */}
        <div className="dash-card col-span-1 md:col-span-6">
          <DiscomLoad
            data={data.discomLoad
              .map((item) => ({
                ...item,
                type: "Actual",
              }))
              .concat(
                data.forecastDiscomLoad.map((item) => ({
                  ...item,
                  type: "Forecast",
                })),
              )}
          />
        </div>
        {/* Bottom Right */}
        {/* <div className="flex justify-between col-span-1 md:col-span-12">
          <p className="gna-footer-text">Source - IGX</p>
        </div> */}
      </div>
      <div className="gna-footer-container">
        <div className="flex justify-between ">
          <p className="gna-footer-text">Source - Discom, Discom, WBES</p>
          <p className="gna-footer-text">Powered by GNA Energy</p>
          <p className="gna-footer-text w-60"></p>
        </div>
        <p className="gna-footer-note">
          Note: Short Term Procurement % = (Collective + TGNA)/Total Discom
          Load; (2) Interstate Generation = WBES schedule; (3) Intrastate/RE
          Generation = Discom schedule (4) Wt. Avg Mrkt Price is given where
          Discom price data is not available
        </p>
      </div>
    </>
  );
}

function SummaryChart({
  data,
  discomData,
}: {
  data: CompareRajasthan[];
  discomData: RajasthanDiscomData[];
}) {
  const [chartData, setChartData] = useState<ChartData>();
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [selectedEntity, setSelectedEntity] = useState<string[]>([]);
  const [selectedConsumption, setSelectedConsumption] = useState(
    CONSUMPTION[0],
  );
  const getSelectedUnit = () => (selectedKey.isTime ? UNITS[0] : UNITS[1]);

  const uniqueEntities = Array.from(
    new Set(data.map((item) => [item.entity, item.toEntity]).flat()),
  ).filter((item) => item !== undefined) as string[];

  const LEGENDS = [
    {
      key: "ISGS",
      label: "ISGS Gen.",
      color: ExchangeColors[3],
      stackId: "generation",
    },

    {
      key: "Intra Generation",
      label: "Intra Gen.",
      color: ExchangeColors[0],
      stackId: "generation",
    },
    {
      key: "IntraState RE Generation",
      label: "Intra RE Gen.",
      color: "#00FF00",
      stackId: "generation",
    },
    {
      key: "Inter RE Gen.",
      label: "OA RE Buy",
      color: "green",
      stackId: "generation",
    },
  ];

  useEffect(() => {
    const processedDiscomData = discomData.map((item) => ({
      ...item,
      type: "Total Discom",
      value_mwh: (item.drawal_mw + item.load_shedding_mw) / 4,
      value_mw: item.drawal_mw + item.load_shedding_mw,
      time_block: item.block,
      month: getMonthFromDate(item.date),
    }));

    let filteredData = data.concat(processedDiscomData);
    if (selectedEntity.length > 0) {
      filteredData = filteredData.filter(
        (item) =>
          selectedEntity.includes(item.entity!) ||
          selectedEntity.includes(item.toEntity!),
      );
    }
    const par: ChartDataParams = {
      keys: selectedKey.secondKey
        ? [selectedKey.firstKey, selectedKey.secondKey]
        : [selectedKey.firstKey],
      groupBy: ["type"],
      volumeKey: "value_mwh",
      reverseSort: ["date", "time_block"],
      aggregationKeys: ["value_mw"],
      priceKey: "",
      data: filteredData,
    };

    const res = FormatChartData(par);
    res.data = res.data.map((item) => {
      const total = selectedConsumption.value.reduce((acc, column) => {
        return (
          acc + item[column.key + "_value_" + getSelectedUnit().toLowerCase()]
        );
      }, 0);
      const shortTerm = SHORT_TERM_LEGENDS.filter(
        (e) => e.stackId == "generation",
      )
        .map(
          (legend) => legend.key + "_value_" + getSelectedUnit().toLowerCase(),
        )
        .reduce((acc, column) => {
          return acc + (item[column] ?? 0);
        }, 0);
      const percent = total > 0 ? (shortTerm / total) * 100 : 0;
      const dataItems = data.filter((dataItem) => {
        return (
          item.time_slot === dataItem.time_block &&
          item.date === dataItem.date &&
          (dataItem.type == "Collective Buy" ||
            dataItem.type == "Collective Sell")
        );
      });

      const iex_dam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "DAM",
      ) as any;
      const iex_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" && dataItem.product__name === "RTM",
      ) as any;
      const iex_gdam = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "IEX" &&
          dataItem.product__name === "GDAM",
      ) as any;
      const pxil_rtm = dataItems.find(
        (dataItem: any) =>
          dataItem.exchange__name === "PXIL" &&
          dataItem.product__name === "RTM",
      ) as any;

      return {
        ...item,
        shortTerm: percent,
        IEX_DAM_value_mw: iex_dam ? iex_dam.wt_mcp_rs_kwh : 0,
        IEX_RTM_value_mw: iex_rtm ? iex_rtm.wt_mcp_rs_kwh : 0,
        IEX_GDAM_value_mw: iex_gdam ? iex_gdam.wt_mcp_rs_kwh : 0,
        PXIL_RTM_value_mw: pxil_rtm ? pxil_rtm.wt_mcp_rs_kwh : 0,
      };
    });
    setChartData(res);
  }, [data, selectedKey, selectedEntity]);
  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  return (
    <div className="h-full w-full flex flex-col">
      <div className="gas-heading mb-4">
        <h2 className="chartHeading">Summary</h2>
        <FilterTab
          options={KEYS.map((item) => item.name)}
          selected={[selectedKey.name]}
          onClick={(item, index) => {
            handleKeyChange(index);
          }}
        />
        <div className="min-w-40">
          <SearchBox
            options={uniqueEntities.map((item) => ({
              label: item,
              value: item,
            }))}
            isMany={true}
            placeholder="Search Entities"
            selectedOptions={selectedEntity.map((item) => ({
              label: item,
              value: item,
            }))}
            onChange={(item: any) => {
              setSelectedEntity(item.map((item: any) => item.value));
            }}
          />
        </div>
      </div>

      <ReMixChart
        data={chartData?.data || []}
        xDataKey={selectedKey.firstKey}
        xTick={
          selectedKey.secondKey !== undefined
            ? (value: any, index: any) => {
                return renderHourTick(value, 10);
              }
            : undefined
        }
        secondXDataKey={selectedKey.secondKey}
        unit={getSelectedUnit()}
        isTimeSlot={selectedKey.firstKey === "time_block"}
        onlyTitle={true}
        secondYAxisLabel="%"
        barCategoryGap={12}
        legendFontSize={10}
        yAxisWidth={45}
        secondYAxisWidth={35}
        fontSize={10}
        xaxisHeight={selectedKey.secondKey ? 40 : 20}
        yAxisLabel={getSelectedUnit()}
        showBrush={getShowBrush(chartData?.data || [])}
        brushIndex={getBrushIndex()}
        legends={getActiveLegends()}
      />
    </div>
  );
  function getActiveLegends() {
    const activityLegends = selectedConsumption.value
      .concat(LEGENDS)
      .concat(SHORT_TERM_LEGENDS);

    return activityLegends
      .map((legend) => {
        return {
          dataKey: legend.key + "_value_" + getSelectedUnit().toLowerCase(),
          name: legend.label,
          stroke: legend.color,
          stackId: legend.stackId,
          type: ChartType.Bar,
        };
      })
      .concat([
        {
          dataKey: "shortTerm",
          name: "Short Term Proc. %",
          yAxisId: "right",
          stroke: lightenColor(ExchangeColors[2], 20),
          stackId: "generation",
          type: ChartType.Line,
        } as any,
      ]) as LegendKey[];
  }

  function getShowBrush(chartData: any[]): boolean {
    if (selectedKey.firstKey === "time_block") {
      return chartData.length > 96 * 1;
    } else {
      return chartData.length > 8;
    }
  }

  function getBrushIndex(): { startIndex: number; endIndex: number } {
    if (selectedKey.firstKey === "time_block") {
      return { startIndex: 0, endIndex: 96 * 1 };
    } else {
      return { startIndex: 0, endIndex: 8 };
    }
  }
}
function ProcurementChart({ data }: { data: CompareRajasthan[] }) {
  const [chartData, setChartData] = useState<ChartData>();
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [selectedEntity, setSelectedEntity] = useState<string[]>([]);

  const getSelectedUnit = () => (selectedKey.isTime ? UNITS[0] : UNITS[1]);

  const uniqueEntities = Array.from(
    new Set(data.map((item) => [item.entity, item.toEntity]).flat()),
  ).filter((item) => item !== undefined) as string[];

  useEffect(() => {
    let filteredData = data;
    if (selectedEntity.length > 0) {
      filteredData = filteredData.filter(
        (item) =>
          selectedEntity.includes(item.entity!) ||
          selectedEntity.includes(item.toEntity!),
      );
    }
    const par: ChartDataParams = {
      keys: selectedKey.secondKey
        ? [selectedKey.firstKey, selectedKey.secondKey]
        : [selectedKey.firstKey],
      groupBy: ["exchange", "product"],
      volumeKey: "value_mwh",
      aggregationKeys: ["value_mw"],
      priceKey: "price_kwh",
      data: filteredData,
    };

    const res = FormatChartData(par);
    setChartData(res);
  }, [data, selectedKey, selectedEntity]);
  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  return (
    <div className="h-full w-full flex flex-col">
      <div className="gas-heading mb-4">
        <h2 className="chartHeading">Short Term Procurement</h2>
        <FilterTab
          options={KEYS.map((item) => item.name)}
          selected={[selectedKey.name]}
          onClick={(item, index) => {
            handleKeyChange(index);
          }}
        />
        {/* <div className="min-w-40">
          <SearchBox
            options={uniqueEntities.map((item) => ({
              label: item,
              value: item,
            }))}
            isMany={true}
            placeholder="Search Entities"
            selectedOptions={selectedEntity.map((item) => ({
              label: item,
              value: item,
            }))}
            onChange={(item: any) => {
              setSelectedEntity(item.map((item: any) => item.value));
            }}
          />
        </div> */}
      </div>

      <ReMixChart
        data={chartData?.data || []}
        xDataKey={selectedKey.firstKey}
        xTick={
          selectedKey.secondKey !== undefined
            ? (value: any, index: any) => {
                return renderHourTick(value, 10);
              }
            : undefined
        }
        secondXDataKey={selectedKey.secondKey}
        unit={getSelectedUnit()}
        isTimeSlot={selectedKey.firstKey === "time_block"}
        onlyTitle={true}
        secondYAxisLabel="%"
        barCategoryGap={12}
        legendFontSize={10}
        overflowLegend={true}
        yAxisWidth={45}
        secondYAxisWidth={35}
        fontSize={10}
        xaxisHeight={selectedKey.secondKey ? 40 : 20}
        yAxisLabel={getSelectedUnit()}
        showBrush={getShowBrush(chartData?.data || [])}
        brushIndex={getBrushIndex()}
        legends={getActiveLegends()}
      />
    </div>
  );
  function getActiveLegends() {
    const activityLegends = [
      {
        key: "IEX_RTM",
        label: "IEX RTM",
        color: ExchangeColors[1],
        stackId: "generation",
      },
      {
        key: "IEX_DAM",
        label: "IEX DAM",
        color: ExchangeColors[0],
        stackId: "generation",
      },
      {
        key: "IEX_GDAM",
        label: "IEX GDAM",
        color: ExchangeColors[4],
        stackId: "generation",
      },
      {
        key: "PXIL_RTM",
        label: "PXIL RTM",
        color: "purple",
        stackId: "generation",
      },
      {
        key: "PXIL_TAM",
        label: "PXIL TAM",
        color: ExchangeColors[2],
        stackId: "generation",
      },

      {
        key: "IEX_TAM",
        label: "IEX TAM",
        color: "blue",
        stackId: "generation",
      },

      {
        key: "HPX_TAM",
        label: "HPX TAM",
        color: "green",
        stackId: "generation",
      },
      { key: "IEX_LDC", label: "IEX LDC", color: "red", stackId: "generation" },
      {
        key: "PXIL_LDC",
        label: "PXIL LDC",
        color: "orange",
        stackId: "generation",
      },
      {
        key: "HPX_LDC",
        label: "HPX LDC",
        color: "brown",
        stackId: "generation",
      },

      {
        key: "Banking_Banking",
        label: "Banking",
        color: "brown",
        stackId: "generation",
      },

      {
        key: "Deep_RA",
        label: "DEEP RA",
        color: "blue",
        stackId: "generation",
      },
    ];

    return activityLegends
      .map((legend) => {
        return {
          dataKey: legend.key + "_value_" + getSelectedUnit().toLowerCase(),
          name: legend.label + " (" + getSelectedUnit() + ")",
          stroke: legend.color,
          stackId: "pr",
          type: ChartType.Bar,
        };
      })
      .concat(
        activityLegends.map((legend) => {
          return {
            dataKey: legend.key + "_price_kwh",
            name: legend.label + " (Rs/kWh)",
            stroke: legend.color.includes("#")
              ? lightenColor(legend.color, 0.7)
              : legend.color,
            type: ChartType.Line,
            stackId: "pr",
            yAxisId: "right",
          };
        }),
      ) as LegendKey[];
  }

  function getShowBrush(chartData: any[]): boolean {
    if (selectedKey.firstKey === "time_block") {
      return chartData.length > 96 * 1;
    } else {
      return chartData.length > 8;
    }
  }

  function getBrushIndex(): { startIndex: number; endIndex: number } {
    if (selectedKey.firstKey === "time_block") {
      return { startIndex: 0, endIndex: 96 * 1 };
    } else {
      return { startIndex: 0, endIndex: 8 };
    }
  }
}

function GenerationSplit({
  interGen,
  intraGen,
  reGen,
}: {
  interGen: RajasthanIsgs[];
  reGen: ReEnergyData[];
  intraGen: RajasthanGenData[];
}) {
  const re = reGen
    .filter((i) => i.energy_type !== "")
    .map((item) => ({
      ...item,
      area: item.area == "InterState" ? "Inter State" : "Intra State",
      station: "RE " + item.area,
      source: titleCase(item.energy_type),
      slot: item.time_block,
    }));
  const intra = intraGen.map((item) => ({
    ...item,
    area: "Intra State",
    station: item.station,
    source: titleCase(item.source),
    slot: item.slot,
  }));
  const inter = interGen.map((item) => ({
    ...item,
    area: "Inter State",
    station: item.station,
    source: titleCase(item.from_gen__generation_source),
    slot: item.slot,
  }));
  interface Data {
    date: string;
    slot: number;
    area: string;
    station: string;
    source: string;
    value_mwh?: number;
  }
  const data: Data[] = intra.concat(re as any).concat(inter as any);

  const keys = [
    { name: "Area", key: "area", groupBy: ["area"] },
    { name: "Station", key: "station", groupBy: ["station"] },
    { name: "Source", key: "source", groupBy: ["source", "area"] },
  ];

  const units = [
    { name: "MWh", key: "value_mwh" },
    { name: "Percent", key: "percent" },
  ];

  const [selectedKey, setSelectedKey] = useState(keys[2]);
  const [selectedUn, setSelectedUn] = useState(units[0]);
  const [chartData, setChartData] = useState<ChartData>();
  useEffect(() => {
    const par: ChartDataParams = {
      keys: [selectedKey.key],
      groupBy: [],
      volumeKey: "value_mwh",
      aggregationKeys: ["value_mwh"],
      priceKey: "",
      data: data,
    };
    const res = FormatChartData(par);
    const total = res.data.reduce((acc, item) => {
      return acc + item["_value_mwh"];
    }, 0);
    res.data = res.data
      .map((item) => {
        const key_percent = {
          ...res.keys.reduce(
            (acc, column) => {
              acc[column + "_percent"] =
                (item[column + "_value_mwh"] / total) * 100;
              return acc;
            },
            {} as Record<string, any>,
          ),
        };
        return {
          ...item,
          ...key_percent,
        };
      })
      .sort((a, b) => b["_value_mwh"] - a["_value_mwh"]);
    setChartData(res);
  }, [selectedKey, intraGen, interGen, reGen]);

  return (
    <div className="h-full w-full flex flex-col">
      <div className="gas-heading mb-4">
        <h2 className="chartHeading">Generation Split</h2>
        <FilterTab
          options={keys.map((item) => item.name)}
          selected={[selectedKey.name]}
          onClick={(item, index) => {
            setSelectedKey(keys[index]);
          }}
        />
      </div>
      <ReMixChart
        data={chartData?.data || []}
        xDataKey={selectedKey.key}
        unit="MWh"
        secondYAxisLabel="Generation %"
        isTimeSlot={false}
        onlyTitle={true}
        barCategoryGap={12}
        secondYAxisWidth={40}
        brushIndex={{ startIndex: 0, endIndex: 7 }}
        yAxisWidth={65}
        labelFontSize={11}
        fontSize={11}
        xaxisHeight={(chartData?.data || []).length > 10 ? 30 : 20}
        yAxisLabel="MWh"
        showBrush={(chartData?.data || []).length > 10}
        legends={[
          {
            dataKey: "_value_mwh",
            name: "Generation MWh",

            stroke: ExchangeColors[0],
            stackId: "generation",
            type: ChartType.Bar,
          },
          {
            dataKey: "_percent",
            name: "Generation %",
            stroke: ExchangeColors[1],
            stackId: "generation",
            yAxisId: "right",
            type: ChartType.Bar,
          },
        ]}
      />
    </div>
  );
}

function DiscomLoad({ data }: { data: RajasthanDiscomData[] }) {
  const TYPE = ["Drawal"];
  const X_AXIS = [
    {
      label: "Time Slot",
      key: "time",
      values: ["block", "date"],
      isTime: true,
    },
    {
      label: "Date",
      key: "date",
      values: ["date"],
      isTime: false,
    },
  ];
  const [chartData, setChartData] = useState<ChartData>({
    data: [],
    keys: [],
  } as any);
  const [selectedInterval, setSelectedInterval] = useState(X_AXIS[0]);

  const chartSuffixes = [
    { suffix: "schedule", displayName: "Schedule", stackSuffix: "schedule" },
    { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
    {
      suffix: "load_shedding",
      displayName: "Load Shedding",
      stackSuffix: "load_shedding",
    },
    { suffix: "total", displayName: "Total Load", stackSuffix: "total" },
  ];

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedXaxis: { isTime: boolean },
    ExchangeColors: string[],
  ) => {
    return {
      dataKey: `${key}_${suffix}_` + (selectedXaxis.isTime ? "mw" : "mwh"),
      name: normalizeDisplayLabel(key),
      legendColor: undefined,
      stroke:
        suffix === "schedule"
          ? lightenColor(ExchangeColors[index], 25)
          : suffix == "drawal"
            ? ExchangeColors[index]
            : stringToConsistentColor(`${key}_${suffix}`),
      type: ChartType.Bar,
      stackId: key.split("_")[1],
    };
  };
  const [selectedSuffix, setSelectedSuffix] = useState([
    // chartSuffixes[0],
    chartSuffixes[1],
  ]);
  useEffect(() => {
    let filteredData = data;

    const par: ChartDataParams = {
      keys: selectedInterval.values,
      groupBy: ["discom", "type"],
      volumeKey: "drawal_mwh",
      aggregationKeys: ["drawal_mw"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData);
  }, [data, selectedInterval]);

  return (
    <div className="h-full w-full flex flex-col">
      <div className="gas-heading mb-4">
        <h2 className="chartHeading">Discom Load</h2>
        <FilterTab
          options={X_AXIS.map((item) => item.label)}
          selected={[selectedInterval.label]}
          onClick={(item, index) => {
            setSelectedInterval(X_AXIS[index]);
          }}
        />
      </div>
      <ReMixChart
        data={chartData?.data || []}
        xTick={
          selectedInterval.isTime
            ? (value: any, index: any) => {
                return renderHourTick(value, 10);
              }
            : undefined
        }
        showBrush={chartData?.data.length > 96 ? true : undefined}
        brushStart={BrushStart.Start}
        isTimeSlot={selectedInterval.isTime}
        onlyTitle={!selectedInterval.isTime}
        barCategoryGap={14}
        yAxisWidth={65}
        secondYAxisWidth={0}
        xaxisHeight={selectedInterval.isTime ? 40 : 20}
        unit={selectedInterval.isTime ? "MW" : "MWh"}
        yAxisLabel={selectedInterval.isTime ? "MW" : "MWh"}
        legends={selectedSuffix.flatMap(
          ({ suffix, displayName, stackSuffix }) =>
            chartData.keys.map(
              (key, index) =>
                createChartObject(
                  key,
                  index,
                  suffix,
                  displayName,
                  stackSuffix,
                  selectedInterval,
                  ExchangeColors,
                ) as any,
            ),
        )}
        xDataKey={selectedInterval.values[0]}
        secondXDataKey={
          selectedInterval.isTime ? selectedInterval.values[1] : undefined
        }
      />
    </div>
  );
}
